import { MdOutlineAccessTime } from 'react-icons/md';
import { useAppContext } from '@features/contexts/AppProvider';
import { LogTypeKeys } from '@features/components/ClinicalData/components/ClinicalLog/types';
import { StatusChangeLabelSize } from '@features/components/ClinicalData/components/ClinicalLog/components/constants';
import { LogItemLayout } from '@features/components/ClinicalData/components/ClinicalLog/components/LogItemLayout';
import { cn } from '@ds';
import { ConditionalRender } from '@ui';
import { LogItemHeader } from '../components/LogItemHeader';

type StatusChangeItemProps = {
  statusLabel: string;
  description?: string;
  shared?: boolean;
  variant?: 'base' | 'maroon';
  showIcon?: boolean;
  onShareClick?: () => void;
};

const cssStylesBySize: Record<
  StatusChangeLabelSize,
  {
    iconSize: number;
    labelClassNames: string;
  }
> = {
  [StatusChangeLabelSize.minor]: {
    iconSize: 14,
    labelClassNames: 'text-s-m',
  },
  [StatusChangeLabelSize.base]: {
    iconSize: 16,
    labelClassNames: 'font-semibold',
  },
};

const cssStylesByStatus = {
  base: {
    bg: 'bg-base-10!',
    iconBg: 'bg-base-20',
    icon: 'text-white',
  },
  maroon: {
    bg: 'bg-maroon-10!',
    iconBg: 'bg-maroon-50',
    icon: 'text-maroon-10',
  },
};

export const StatusChangeItem = ({
  statusLabel,
  description,
  shared,
  variant = 'base',
  showIcon = true,
  onShareClick,
}: StatusChangeItemProps) => {
  const {
    clinicalLog: { styles },
  } = useAppContext();
  const cssStyleByStatus = cssStylesByStatus[variant];
  const cssStyleBySize = cssStylesBySize[styles[LogTypeKeys.StatusChangeItem] as StatusChangeLabelSize];
  return (
    <LogItemLayout className={cn(cssStyleByStatus.bg, 'gap-1')}>
      <LogItemHeader
        onShareClick={onShareClick}
        event={statusLabel}
        startContent={
          <ConditionalRender show={showIcon}>
            <div className={cn('w-5 h-5 flex items-center justify-center rounded-full mr-1', cssStyleByStatus.iconBg)}>
              <MdOutlineAccessTime className={cssStyleByStatus.icon} size={cssStyleBySize.iconSize} />
            </div>
          </ConditionalRender>
        }
        sharedIndicatorIcon={shared}
        sharedVariant="light"
        cssStyleBySize={cssStyleBySize}
      />
      {description && <div className="text-s-r text-white">{description}</div>}
    </LogItemLayout>
  );
};
